import React, { useState } from "react";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/styles";
import { graphql } from "react-apollo";
import gql from "graphql-tag";
import { LinearProgress, CircularProgress } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { FileDownload } from "styled-icons/fa-solid/FileDownload";
import GenerateTagsDialog from "./GenerateTagsDialog";

const TAG_SETS = gql`
  {
    tagSets {
      id
      n
      startTag
      colors
      createdAt
      testing
      download
    }
    currentProject {
      id
      lastTagGenerated {
        id
      }
    }
  }
`;

const useStyles = makeStyles(theme => ({
  card: {
    maxWidth: 560,
    margin: "10px 0",
    [theme.breakpoints.down("sm")]: {
      width: "100vw",
      margin: 0,
      borderRadius: 0,
      boxShadow: 'none',
    }
  },
  tableCell: {
    padding: "4px 22px 4px 22px"
  },
  tagsTableCell: {
    // padding: "4px 64px 4px 64px"
    padding: "4px 24px 4px 24px",
    whiteSpace: "nowrap"
  },
  root: {
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: 0
    }
  }
}));

const TagsPage = ({
  data: { loading, error, tagSets, currentProject, refetch }
}) => {
  const classes = useStyles();
  const [generateTagsDialogOpen, setGenerateTagsDialogOpen] = useState(false);
  if (loading) return <CircularProgress size={20} />;
  if (error) return <div>{error.toString()}</div>;

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <Typography gutterBottom variant="h5" component="h2">
            Tags
          </Typography>
          <Typography gutterBottom component="p">
            Identifiers are generated and tracked by the application to avoid
            problems with duplicates or color mismatches. As more tags are
            needed, tag id's and qr-code contents can be generated here as CSV
            files that can be provided to the{" "}
            <a href="http://www.growers-inc.com/contact/">printer</a>.
          </Typography>
          <Typography gutterBottom component="p">
            It is crucial to accurately track what has been distributed. Please
            avoid using this interface unless you have been trained on how to do
            so.
          </Typography>
          <Typography style={{ marginTop: 20 }} variant="body1">
            {currentProject.lastTagGenerated
              ? currentProject.lastTagGenerated.id
              : 0}{" "}
            of 99,999 tags generated
          </Typography>
          <LinearProgress
            variant="determinate"
            value={
              currentProject.lastTagGenerated
                ? (currentProject.lastTagGenerated.id / 99999) * 100
                : 0
            }
          />
        </CardContent>
        <CardActions>
          <Button
            variant="text"
            color="primary"
            onClick={() => setGenerateTagsDialogOpen(true)}
          >
            Generate More Tags
          </Button>
        </CardActions>
      </Card>
      <Card className={classes.card}>
        <CardContent>
          <Typography gutterBottom variant="h5" component="h2">
            Downloads
          </Typography>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCell}>Generated</TableCell>
                <TableCell align="center" className={classes.tagsTableCell}>
                  Tags
                </TableCell>
                <TableCell align="center" className={classes.tableCell}>
                  Colors
                </TableCell>
                <TableCell align="center" className={classes.tableCell}>
                  Testing
                </TableCell>
                <TableCell align="center" className={classes.tableCell}>
                  Download
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tagSets.map(row => (
                <TableRow key={row.id}>
                  <TableCell align="center" className={classes.tableCell}>
                    {new Date(row.createdAt).toLocaleDateString("en-US")}
                  </TableCell>
                  <TableCell align="center" className={classes.tagsTableCell}>
                    {row.startTag}-{row.startTag + row.n - 1}
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    {row.colors.length}
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    {row.testing ? "yes" : "no"}
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    <Button>
                      <FileDownload size={16} color="grey" onClick={() => window.open(row.download)} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <GenerateTagsDialog
        open={generateTagsDialogOpen}
        onClose={() => refetch() && setGenerateTagsDialogOpen(false)}
      />
    </div>
  );
};

export default graphql(TAG_SETS, { options: { pollInterval: 10000 } })(
  TagsPage
);
